import { useEffect, useRef } from "react";

const VERT = `#version 300 es
precision highp float;
in vec2 a_pos;
out vec2 v_uv;
void main(){
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 frag;

uniform vec2  u_res;
uniform float u_time;
uniform vec2  u_mouse;
uniform float u_aspect;
uniform float u_pixel;

// classic 2d hash + value noise
float hash(vec2 p){
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  vec2 u = f*f*(3.0 - 2.0*f);
  return mix(mix(a,b,u.x), mix(c,d,u.x), u.y);
}
float fbm(vec2 p){
  float s = 0.0, a = 0.5;
  mat2 rot = mat2(0.8, -0.6, 0.6, 0.8);
  for (int i = 0; i < 6; i++){
    s += a * vnoise(p);
    p = rot * p * 2.02;
    a *= 0.5;
  }
  return s;
}

// Bayer 8x8 ordered dither — normalized 0..1 pattern
float bayer8(vec2 p){
  const float m[64] = float[64](
     0.0,32.0, 8.0,40.0, 2.0,34.0,10.0,42.0,
    48.0,16.0,56.0,24.0,50.0,18.0,58.0,26.0,
    12.0,44.0, 4.0,36.0,14.0,46.0, 6.0,38.0,
    60.0,28.0,52.0,20.0,62.0,30.0,54.0,22.0,
     3.0,35.0,11.0,43.0, 1.0,33.0, 9.0,41.0,
    51.0,19.0,59.0,27.0,49.0,17.0,57.0,25.0,
    15.0,47.0, 7.0,39.0,13.0,45.0, 5.0,37.0,
    63.0,31.0,55.0,23.0,61.0,29.0,53.0,21.0
  );
  int x = int(mod(p.x, 8.0));
  int y = int(mod(p.y, 8.0));
  return (m[y * 8 + x] + 0.5) / 64.0;
}

void main(){
  // quantize to fake a low-res render so the dither shows as real pixels
  vec2 px = floor(gl_FragCoord.xy / u_pixel);
  vec2 uv = px * u_pixel / u_res;
  uv.x *= u_aspect;

  // flowing field driven by time + mouse pull
  vec2 p = uv * 2.2;
  vec2 mouse = (u_mouse / u_res) * vec2(u_aspect, 1.0);
  float pull = smoothstep(0.45, 0.0, distance(uv, mouse));

  vec2 q = vec2(fbm(p + u_time * 0.04),
                fbm(p + vec2(5.2, 1.3) + u_time * 0.05));
  vec2 r = vec2(fbm(p + 4.0 * q + vec2(1.7, 9.2) - u_time * 0.03),
                fbm(p + 4.0 * q + vec2(8.3, 2.8) + u_time * 0.035));
  float v = fbm(p + 4.0 * r);

  // radial vignette + mouse bloom
  vec2 c = uv - vec2(u_aspect * 0.5, 0.5);
  float vign = 1.0 - smoothstep(0.25, 0.95, length(c));
  v = v * 0.8 + 0.22 + pull * 0.35 + vign * 0.08;

  // 5-stop tonal quantization with Bayer threshold shift
  float t = bayer8(px);
  float levels = 5.0;
  float q5 = floor(v * levels + t) / levels;

  // palette: deep ink -> violet -> magenta -> rose -> cream
  vec3 c0 = vec3(0.043, 0.031, 0.078);
  vec3 c1 = vec3(0.129, 0.067, 0.329);
  vec3 c2 = vec3(0.498, 0.133, 0.549);
  vec3 c3 = vec3(0.945, 0.360, 0.580);
  vec3 c4 = vec3(0.988, 0.941, 0.875);

  vec3 col;
  if (q5 < 0.25)       col = mix(c0, c1, q5 / 0.25);
  else if (q5 < 0.50)  col = mix(c1, c2, (q5 - 0.25) / 0.25);
  else if (q5 < 0.75)  col = mix(c2, c3, (q5 - 0.50) / 0.25);
  else                 col = mix(c3, c4, clamp((q5 - 0.75) / 0.25, 0.0, 1.0));

  // scanline whisper for texture
  float scan = 0.5 + 0.5 * sin((px.y) * 0.35);
  col *= 0.97 + 0.03 * scan;

  frag = vec4(col, 1.0);
}`;

function makeShader(gl: WebGL2RenderingContext, type: number, src: string) {
  const s = gl.createShader(type);
  if (!s) throw new Error("createShader failed");
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s);
    gl.deleteShader(s);
    throw new Error("shader compile: " + log);
  }
  return s;
}

function makeProgram(gl: WebGL2RenderingContext, vs: string, fs: string) {
  const p = gl.createProgram();
  if (!p) throw new Error("createProgram failed");
  gl.attachShader(p, makeShader(gl, gl.VERTEX_SHADER, vs));
  gl.attachShader(p, makeShader(gl, gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    throw new Error("program link: " + gl.getProgramInfoLog(p));
  }
  return p;
}

export function DitherCanvas({ className = "", pixelSize = 3 }: { className?: string; pixelSize?: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl2", { antialias: false, preserveDrawingBuffer: false });
    if (!gl) return;

    let program: WebGLProgram;
    try { program = makeProgram(gl, VERT, FRAG); }
    catch (e) { console.error(e); return; }

    const quad = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);

    const locPos = gl.getAttribLocation(program, "a_pos");
    const locRes = gl.getUniformLocation(program, "u_res");
    const locTime = gl.getUniformLocation(program, "u_time");
    const locMouse = gl.getUniformLocation(program, "u_mouse");
    const locAspect = gl.getUniformLocation(program, "u_aspect");
    const locPixel = gl.getUniformLocation(program, "u_pixel");

    gl.useProgram(program);
    gl.enableVertexAttribArray(locPos);
    gl.vertexAttribPointer(locPos, 2, gl.FLOAT, false, 0, 0);

    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    function onMove(e: PointerEvent) {
      const rect = canvas!.getBoundingClientRect();
      mouse.tx = (e.clientX - rect.left) * devicePixelRatio;
      mouse.ty = (rect.height - (e.clientY - rect.top)) * devicePixelRatio;
    }
    window.addEventListener("pointermove", onMove, { passive: true });

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.floor(canvas!.clientWidth * dpr);
      const h = Math.floor(canvas!.clientHeight * dpr);
      if (canvas!.width !== w || canvas!.height !== h) {
        canvas!.width = w; canvas!.height = h;
        gl!.viewport(0, 0, w, h);
      }
    }
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    const start = performance.now();
    let raf = 0;
    let stopped = false;
    function frame() {
      if (stopped) return;
      resize();
      mouse.x += (mouse.tx - mouse.x) * 0.08;
      mouse.y += (mouse.ty - mouse.y) * 0.08;
      gl!.uniform2f(locRes, canvas!.width, canvas!.height);
      gl!.uniform1f(locTime, (performance.now() - start) / 1000);
      gl!.uniform2f(locMouse, mouse.x, mouse.y);
      gl!.uniform1f(locAspect, canvas!.width / canvas!.height);
      gl!.uniform1f(locPixel, pixelSize * (window.devicePixelRatio || 1));
      gl!.drawArrays(gl!.TRIANGLE_STRIP, 0, 4);
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      gl.deleteProgram(program);
      gl.deleteBuffer(buf);
    };
  }, [pixelSize]);

  return <canvas ref={ref} className={className} />;
}
